import { ABCitySelect, ABVenueSelect } from '../lib/ab/select-admin';
import { ab_transfer_destination_kind_values, ABNewPicturesArray, makeTransferDestinationInput } from '../lib/ab/widgets-admin';
import { c_cities } from '../lib/mdl/c-city';
import { cCountryDefaultCurrency } from '../lib/mdl/c-country';
import { cSessionDefaultInstructions } from '../lib/mdl/c-event-admin';
import { c_event_tags, cEventTagName } from '../lib/mdl/c-event-tag';
import { c_producers } from '../lib/mdl/c-producer';
import { cProducerUpdateEventDefaults } from '../lib/mdl/c-producer-admin';
import { uInputTranslator } from '../lib/mdl/u-translate';
import { haveRoleInProducer } from '../lib/me/can-producer';
import { the_user } from '../lib/me/login';
import { meChangePassword, meUpdate } from '../lib/me/me';
import { the_producer } from '../lib/me/producer';
import { UIButton } from '../lib/ui/component/button';
import { UINewI18NInput } from '../lib/ui/component/i18n-input';
import { UIFixedCurrencyInput, UINumberInput, UITextInput } from '../lib/ui/component/input';
import { UIAutofill } from '../lib/ui/component/input-base';
import { UILabeled } from '../lib/ui/component/labeled';
import { UIMarkdownInput } from '../lib/ui/component/markdown';
import { UISelect } from '../lib/ui/component/select';
import { UITab, UITabs } from '../lib/ui/component/tabs';
import { uiToast } from '../lib/ui/component/toast';
import { UIExclusiveToggles, UITickRadio } from '../lib/ui/component/toggle';
import { $el } from '../lib/ui/elements';
import { UIScreen } from '../lib/ui/screen';
import { UIView, UIViewSpec } from '../lib/ui/view';
import { $analytics } from '../lib/y/analytics';
import { $on } from '../lib/y/events';
import { $fn } from '../lib/y/fn';
import { $form } from '../lib/y/form';
import { $i18n } from '../lib/y/i18n';

class UserInfoTab extends UIView {
    constructor() {
        super('div', 'tl-new-producer-page');

        const first_name = new UITextInput({ kind: 'k-subtle', required: true });
        const last_name = new UITextInput({ kind: 'k-subtle', required: true });
        const email = new UITextInput({ kind: 'k-subtle', type: 'email', required: true });
        const phone = new UITextInput({ kind: 'k-subtle', type: 'phone', required: true });
        const user_form = $form({ first_name, last_name, email, phone });
        const save = new UIButton({
            title: $i18n.tl.b_save, classes: 'm-round', on_click: () => {
                if(!user_form.validate()) return;
                return meUpdate(user_form.value).then($fn.TRUE, uiToast);
            }
        });

        const old_password = new UITextInput({ kind: 'k-subtle', type: 'password', autofill: UIAutofill.OLD_PASSWORD, required: true });
        const new_password = new UITextInput({ kind: 'k-subtle', type: 'password', autofill: UIAutofill.NEW_PASSWORD, required: true });
        const change = new UIButton({
            title: $i18n.tl.b_change_pw, classes: 'm-round', on_click: () => {
                if(!$form.validate([old_password, new_password])) return;
                $analytics.track('Changed Password');
                return meChangePassword(old_password.value, new_password.value).then($fn.TRUE, uiToast);
            }
        });

        user_form.value = the_user;

        this.ui_add($el.FORM('ui-section w-producer-form l-form tl-producer-form', [
            new UILabeled(first_name, { above: $i18n.tl.ph_first_name }),
            new UILabeled(last_name, { above: $i18n.tl.ph_last_name }),
            new UILabeled(email, { above: $i18n.tl.ph_email }),
            new UILabeled(phone, { above: $i18n.tl.ph_phone }),
            $el.DIV('l-button-row', [save]),
            $el.H3('heading', 'Change password'),
            new UILabeled(old_password, { above: $i18n.tl.ph_current_pw }),
            new UILabeled(new_password, { above: $i18n.tl.ph_new_pw }),
            $el.DIV('l-button-row', [change])
        ]));
    }
}

class ProducerInfoTab extends UIView {
    constructor() {
        super('div', 'tl-admin-page');

        const org_name = new UITextInput({ kind: 'k-subtle' });
        const org_number = new UITextInput({ kind: 'k-subtle' });
        const address = new UITextInput({ kind: 'k-subtle', lines: 3 });
        const payout_destination_kind = new UIExclusiveToggles(ab_transfer_destination_kind_values, v => new UITickRadio({ ...v }), { classes: 'l-toggle-row' });
        const payout_destination_number = makeTransferDestinationInput(payout_destination_kind, { kind: 'k-subtle' });

        const form = $form({ org_name, org_number, address, payout_destination: $form({ kind: payout_destination_kind, number: payout_destination_number }) });

        const save = new UIButton({ title: $i18n.tl.b_save, classes: 'm-round' });

        this.ui_add($el.FORM('ui-section w-producer-form l-form tl-producer-form', [
            new UILabeled(org_name, { above: $i18n.aa.f_company_name }),
            new UILabeled(org_number, { above: $i18n.aa.f_org_number }),
            new UILabeled(address, { above: $i18n.aa.f_address }),
            $el.H3('heading', $i18n.pr.account_payment_details),
            $el.P('input-hint', $i18n.pr.account_payment_details_description),
            payout_destination_kind,
            new UILabeled(payout_destination_number, { above: $i18n.pr.account_account_number }),
            $el.DIV('l-button-row', save)
        ]));

        const producer = the_producer!;

        save.on_click = () => {
            if(!form.validate()) return;
            $analytics.track('Producer Update');
            return c_producers.update(producer.id, form.value).then($fn.TRUE, uiToast);
        };

        form.value = producer;
    }
}

class DefaultsTab extends UIView {
    constructor() {
        super('div', 'tl-admin-page');

        const city_id = new ABCitySelect({ kind: 'k-subtle', for_producers: true });
        const venue_id = new ABVenueSelect({ kind: 'k-subtle' });
        $on(city_id, 'value', city => {
            if(city) {
                venue_id.city = city;
                base_price.value = { value: base_price.value.value, currency: cCountryDefaultCurrency(c_cities.maybe(city)!.country) };
            }
        })
        const genre_values = c_event_tags.all.filter(t => t.is_genre).map(t => ({ key: t.id, name: cEventTagName(t) }));
        const genre = new UISelect(genre_values, { kind: 'k-subtle', searchable: genre_values.length > 10 });
        const description = new UINewI18NInput(new UIMarkdownInput({ lines: 8, resizeable: true, kind: 'k-subtle' }), { button_classes: 'ui-tint-fg', translator: uInputTranslator });
        const picture_ids = new ABNewPicturesArray('+ Add picture', 'event', false, true);
        const ticket_values = [{ key: 'email', title: 'E-ticket' }, { key: 'pickup', title: 'Guestlist' }];
        const ticket_type = new UIExclusiveToggles(ticket_values, v => new UITickRadio({ ...v }), { classes: 'l-toggle-row' });
        const sales_end = new UINumberInput({ kind: 'k-subtle', decimal: true });
        const instructions = new UINewI18NInput(new UITextInput({ kind: 'k-subtle', lines: 3 }), { button_classes: 'ui-tint-fg', translator: uInputTranslator });
        const base_price = new UIFixedCurrencyInput({ kind: 'k-subtle', default_currency: cCountryDefaultCurrency(the_user!.country) });

        const form = $form({ _: [genre], city_id, venue_id, description, picture_ids: picture_ids.adapter, sales_end, instructions, base_price, ticket_type });

        const save = new UIButton({ title: $i18n.tl.b_save, classes: 'm-round' });

        this.ui_add($el.FORM('ui-section w-producer-form l-form tl-producer-form', [
            new UILabeled(city_id, { above: $i18n.aa.f_city }),
            new UILabeled(venue_id, { above: $i18n.aa.f_venue }),
            new UILabeled(genre, { above: $i18n.aa.f_genre }),
            new UILabeled(description, { above: $i18n.aa.f_description }),
            new UILabeled(picture_ids, { above: $i18n.aa.f_pictures }),
            picture_ids.add_button,
            new UILabeled(ticket_type, { above: $i18n.pr.session_ticket_type }),
            new UILabeled(sales_end, { above: $i18n.aa.f_sales_end }),
            new UILabeled(instructions, { above: $i18n.aa.f_instructions }),
            new UILabeled(base_price, { above: $i18n.aa.f_regular_price }),
            $el.DIV('l-button-row', save)
        ]));

        const producer = the_producer!;

        save.on_click = () => {
            if(!form.validate()) return;

            const value = form.value;
            const genre_value = genre.value;
            if(genre_value)
                value.event_tag_ids = [genre_value];
            $analytics.track('Producer Update');
            return cProducerUpdateEventDefaults(producer, value).then($fn.TRUE, uiToast);
        };

        const defaults = producer.event_defaults || {};
        form.value = defaults;
        const genre_tags = (defaults.event_tag_ids || []).filter(t => {
            const tag = c_event_tags.maybe(t);
            return tag && tag.is_genre;
        });
        genre.value = genre_tags.length ? genre_tags[0] : undefined;

        $on(ticket_type, 'value', v => {
            if(v == 'email') {
                sales_end.value = 5;
                instructions.value = cSessionDefaultInstructions('email');
            } else if(v == 'pickup') {
                instructions.value = cSessionDefaultInstructions('pickup');
            }
        });
    }
}

export class ProducerAccountScreen extends UIScreen {
    constructor(private _tab?: string) {
        super();
    }

    protected override fillContent(content: UIViewSpec) {
        const tab = this._tab;

        const tab_spec: UITab[] = [{ title: $i18n.aa.tb_user_info, view: () => new UserInfoTab }];
        if(haveRoleInProducer('economy', the_producer!.id))
            tab_spec.push({ title: $i18n.aa.tb_company_info, view: () => new ProducerInfoTab() });
        if(haveRoleInProducer('events', the_producer!.id))
            tab_spec.push({ title: $i18n.aa.tb_default_info, view: () => new DefaultsTab() });

        const tabs = new UITabs(tab_spec, { classes: 'tl-new-producer-tabs' });

        content.ui_add($el.DIV('ui-section', $el.DIV('tl-new-list-block', [
            $el.P('screen-title', $i18n.pr.account_title),
            $el.P('screen-mobile-title', $i18n.pr.account_mobile_title),
            tabs
        ])));

        if(tab == 'economy')
            tabs.setCurrent(1);
    }
}
